import React from "react";
import { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme, useMediaQuery, Typography, Collapse } from "@mui/material";
import Divider from "@mui/material/Divider";
import { Share } from "@mui/icons-material";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";

//Open Layers
import OlFormatWKT from "ol/format/WKT";
import OlPoint from "ol/geom/Point";

//Custom Components
import MapContext from "@/components/Map/MapContext";
import ObilaznicePointItems from "./ObilaznicePointItems";


//Types
import { DCRecord } from "@/@types/lib/dataController";
import { IObilaznicePane } from "@/@types/views/GIS/map";
import { InfoPaneActionButtons, InfoPaneShareActionButton, InfoPaneUrlActionButton, InfoPaneCommentActionButton, InfoPaneActionButton } from "../PublicActionButtons";
import { Feature } from "ol";
import { Geometry } from "ol/geom";

type ObilaznicePaneType =
  {
    obilaznicePane: IObilaznicePane;
    mobile?: boolean;
    onExplore?: (id: number) => void;
    onAddToRoute?: (coord: number[] | undefined, label: string) => void;
    drawerStage: number;
    pane: number | undefined;
    open: boolean;
    handleShowCommentPane: (newCommentPane: object) => void;
  }

function ObilaznicePane(props: ObilaznicePaneType) {
  const { t } = useTranslation();

  const { mobile, onExplore, onAddToRoute, drawerStage, pane, open, obilaznicePane, handleShowCommentPane } = props;

  const [oldDrawerStage, setOldDrawerStage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [selectedFeature, setSelectedFeature] = useState<Feature<Geometry> | undefined>(undefined);

  const [pointGroups, setPointGroups] = useState<any>(undefined);

  const mapContext = useContext(MapContext);

  const wktFormatter = new OlFormatWKT();

  const textBox = useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const handleClick = (evt: React.MouseEvent<HTMLDivElement> | null, item: DCRecord) => {
    zoomTo(item);
    const id = item["point_id"] as number;
    setSelectedId(id);
    const feature = wktFormatter.readFeature(item["wkt"], {
              dataProjection: "EPSG:3765",
              featureProjection: "EPSG:3857"
            })
    setSelectedFeature(feature);
  };

  const handleAddToRoute = (evt: React.MouseEvent<HTMLButtonElement>, item: DCRecord) => {
    const wkt = item["wkt"];
    // const tip = item["toponim_tip_id"];
    if (wkt) {
      const feat = wktFormatter.readFeature(wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      })

      if (onAddToRoute) {
        const geom = feat.getGeometry();
        if (geom?.getType() === "Point") {
          const coords = (geom as OlPoint).getCoordinates();
          const naziv = item["naziv"] as string;
          onAddToRoute(coords, naziv);
        }
      }
    }
  };

  const zoomTo = (item: DCRecord) => {
    const wkt = item["wkt"];
    if (wkt) {
      const wktFormatter = new OlFormatWKT();
      const feat = wktFormatter.readFeature(wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      })

      const poiId = item["poi_id"] as number;
      if (onExplore && poiId) {
        onExplore(poiId);
      }

      if (mapContext && mapContext.map) {
        const view = mapContext.map.getView();
        const extent = feat.getGeometry()?.getExtent();

        const padding = isMobile ? [20, 20, 300, 20] : [20, 20, 20, 20]
        if (extent)
          view.fit(extent, { padding: padding, duration: 500, maxZoom: 15 });
      }
    }
  };

  useEffect(() => {
    if (pane === 0 && open && drawerStage === oldDrawerStage) {
      if (textBox.current) {
        setTimeout(() => {
          textBox.current?.focus();
        }, 100)
      }
    }
    else if (isMobile && pane === 0 && oldDrawerStage === 0 && drawerStage !== 0) {
      setTimeout(() => {
        textBox.current?.focus();
      }, 100)
    }

    setOldDrawerStage(drawerStage);
  }, [open, pane, drawerStage])


  const textFnNaziv = (item: DCRecord) => {
    const naziv = item["naziv"] as string;
    const naziv_ttoken = item["naziv_ttoken"]
    return naziv_ttoken ? t(naziv_ttoken as string) as string
      : naziv ? naziv
        : null;
  }

  const textFnOpis = (item: DCRecord) => {
    const opis = item["opis"] as string;
    const opis_ttoken = item["opis_ttoken"];

    return opis_ttoken ? t(opis_ttoken as string) as string
      : opis ? opis
        : null;
  }

  const publicSearcPaneItemsCommonProps = {
    onClick: handleClick,
    onAddToRoute: handleAddToRoute,
    selectedId: selectedId
  }

  useEffect(() => {
    let allPoints = obilaznicePane?.data.record?.points as any[];
    let pointGroups: { [key: string]: any[] } = {};
    
    if (allPoints && allPoints !== null) {
      for (let point of allPoints) {
        let key = point.group_name;
        if (!key) {
          continue;
        }
        if (!pointGroups.hasOwnProperty(key)) {
          pointGroups[key] = [];
        }
        pointGroups[key].push(point);

        if (obilaznicePane.selectedPoiId == point.poi_id) {
          handleClick(null, point)
        }
      }
      setPointGroups(pointGroups);
    }

    if (obilaznicePane && !obilaznicePane.selectedPoiId) {
      const feature = wktFormatter.readFeature(allPoints[0]["wkt"], {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      })
      setSelectedFeature(feature);
    }
  }, [obilaznicePane]);

  // @ts-ignore
  const selectedPoi = obilaznicePane?.data.record?.points.find(x => x.point_id === selectedId);
  const selectedPoiId = selectedPoi?.poi_id
  return (
    <>
      <Box width={"100%"} p={2}>
        <Box>
          <Typography variant="h6" component="h2">
            {t(obilaznicePane?.data.record?.naziv as string)}
          </Typography>
          <Typography variant="body1" color={"gray"} component="p">
            {obilaznicePane?.data.record?.hps_drustvo as String}
          </Typography>
        </Box>
        <InfoPaneActionButtons>
          {/* <InfoPaneActionButton
            title_ttoken={"buttons.share"}
            icon={<Share sx={{ transform: "scale(0.8)" }} color="primary" fontSize="large"></Share>}
            onClick={(evt) => { }}
          /> */}
          <InfoPaneShareActionButton
            object_type="obilaznica"
            // @ts-ignore
            infoPane={{data: {...obilaznicePane?.data, feature: selectedFeature}, selectedPoiId: selectedPoiId }}
          />
          {/*}
          <InfoPaneActionButton
            title_ttoken={"buttons.comment"}
            icon={<ChatBubbleOutlineOutlinedIcon sx={{ transform: "scale(0.8)" }} color="primary" fontSize="large" />}
            onClick={(evt) => { }}
          /> */}
          {/* <InfoPaneShareActionButton
          infoPane={infoPane}
          object_type="poi"
        />
        <InfoPaneCommentActionButton
          objekt_type="poi"
          handleGoToComments={handleShowCommentPane}
          // @ts-ignore
          data={infoPane?.data}
        /> */}
          {obilaznicePane?.data.record.info_url !== null ?
            <InfoPaneUrlActionButton
              url={obilaznicePane?.data.record.info_url as string}
            />
            :
            null
          }
        </InfoPaneActionButtons>
      </Box>
      <Divider variant="middle" sx={{ my: 2 }} />
      <List sx={{
        width: "100%",
        bgcolor: "background.paper",
        position: "relative",
        overflow: "auto",
        height: { xs: drawerStage < 2 ? "30vh" : "calc( 100vh - 230px )", md: "calc( 100vh -  255px )" },
        "& ul": { padding: 0 },
        transition: "all 0.3s"
      }}>
        {pointGroups && pointGroups !== null &&  Object.keys(pointGroups).length > 0 ?
          Object.keys(pointGroups).map((objKey) => {
            return <Collapse in={open} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ObilaznicePointItems
                  {...publicSearcPaneItemsCommonProps}
                  grouped={true}
                  groupName={objKey}
                  items={pointGroups[objKey] as DCRecord[]}
                  primaryTextFn={textFnNaziv}
                  secondaryTextFn={textFnOpis}
                  canAddToRoute={true}
                  selectedId={selectedId}
                  onClick={handleClick}
                />
              </List>
            </Collapse>
          })
          : <ObilaznicePointItems
            {...publicSearcPaneItemsCommonProps}
            grouped={false}
            items={obilaznicePane?.data.record?.points as DCRecord[]}
            primaryTextFn={textFnNaziv}
            secondaryTextFn={textFnOpis}
            canAddToRoute={true}
            selectedId={selectedId}
            onClick={handleClick}
          />}
      </List>
    </>
  );
}

export default ObilaznicePane;
